import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../config/api';
import moment from 'moment';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaBook, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

interface Account {
  id: string;
  code: string;
  name: string;
  account_type: string;
}

interface JournalEntryLine {
  id?: string;
  account: string;
  account_name?: string;
  debit: number;
  credit: number;
  description: string;
}

interface JournalEntry {
  id: string;
  entry_number: string;
  date: string;
  description: string;
  reference: string;
  status: 'draft' | 'posted' | 'voided';
  lines: JournalEntryLine[];
  total_debits: number;
  total_credits: number;
  is_balanced: boolean;
  created_by: string;
  created_at: string;
  posted_at?: string;
}

const JournalEntries: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<JournalEntry | null>(null);
  const [formData, setFormData] = useState({
    date: moment().format('YYYY-MM-DD'),
    description: '',
    reference: '',
  });
  const [lines, setLines] = useState<JournalEntryLine[]>([
    { account: '', debit: 0, credit: 0, description: '' },
    { account: '', debit: 0, credit: 0, description: '' },
  ]);

  useEffect(() => {
    loadJournalEntries();
    loadAccounts();
  }, [token]);

  const loadJournalEntries = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await apiCall(
        '/accounting/journal-entries/',
        { method: 'GET' },
        token || undefined
      );
      const data = await response.json();
      setEntries(data.results || data || []);
    } catch (error) {
      console.error('Error loading journal entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    if (!token) return;
    
    try {
      const response = await apiCall(
        '/accounting/chart-of-accounts/',
        { method: 'GET' },
        token || undefined
      );
      const data = await response.json();
      setAccounts(data.results || data || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const calculateTotals = () => {
    const totalDebits = lines.reduce((sum, line) => sum + (parseFloat(String(line.debit)) || 0), 0);
    const totalCredits = lines.reduce((sum, line) => sum + (parseFloat(String(line.credit)) || 0), 0);
    return { totalDebits, totalCredits, isBalanced: Math.abs(totalDebits - totalCredits) < 0.01 };
  };

  const handleCreateOrUpdate = async () => {
    if (!token) return;
    
    const { totalDebits, totalCredits, isBalanced } = calculateTotals();
    
    if (!isBalanced) {
      alert('Journal entry is not balanced. Debits must equal credits.');
      return;
    }

    if (lines.every(line => !line.account)) {
      alert('Please add at least one line item with an account selected.');
      return;
    }

    try {
      const entryData = {
        ...formData,
        lines: lines.filter(line => line.account), // Only include lines with accounts
      };

      const url = editingEntry 
        ? `/accounting/journal-entries/${editingEntry.id}/`
        : '/accounting/journal-entries/';
      
      const method = editingEntry ? 'PATCH' : 'POST';
      
      const response = await apiCall(
        url,
        {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(entryData)
        },
        token || undefined
      );

      if (response.ok) {
        await loadJournalEntries();
        handleCloseModal();
      }
    } catch (error) {
      console.error('Error saving journal entry:', error);
      alert('Failed to save journal entry');
    }
  };

  const handlePost = async (id: string) => {
    if (!token) return;
    if (!confirm('Are you sure you want to post this journal entry? This action cannot be undone.')) return;

    try {
      const response = await apiCall(
        `/accounting/journal-entries/${id}/post/`,
        { method: 'POST' },
        token || undefined
      );

      if (response.ok) {
        await loadJournalEntries();
      }
    } catch (error) {
      console.error('Error posting journal entry:', error);
      alert('Failed to post journal entry');
    }
  };

  const handleVoid = async (id: string) => {
    if (!token) return;
    if (!confirm('Are you sure you want to void this journal entry?')) return;

    try {
      const response = await apiCall(
        `/accounting/journal-entries/${id}/void/`,
        { method: 'POST' },
        token || undefined
      );

      if (response.ok) {
        await loadJournalEntries();
      }
    } catch (error) {
      console.error('Error voiding journal entry:', error);
      alert('Failed to void journal entry');
    }
  };

  const handleDelete = async (id: string) => {
    if (!token) return;
    if (!confirm('Are you sure you want to delete this journal entry?')) return;

    try {
      const response = await apiCall(
        `/accounting/journal-entries/${id}/`,
        { method: 'DELETE' },
        token || undefined
      );

      if (response.ok) {
        await loadJournalEntries();
      }
    } catch (error) {
      console.error('Error deleting journal entry:', error);
      alert('Failed to delete journal entry');
    }
  };

  const handleEdit = (entry: JournalEntry) => {
    setEditingEntry(entry);
    setFormData({
      date: moment(entry.date).format('YYYY-MM-DD'),
      description: entry.description,
      reference: entry.reference,
    });
    setLines(entry.lines.map(line => ({ ...line })));
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingEntry(null);
    setFormData({
      date: moment().format('YYYY-MM-DD'),
      description: '',
      reference: '',
    });
    setLines([
      { account: '', debit: 0, credit: 0, description: '' },
      { account: '', debit: 0, credit: 0, description: '' },
    ]);
  };

  const addLine = () => {
    setLines([...lines, { account: '', debit: 0, credit: 0, description: '' }]);
  };

  const removeLine = (index: number) => {
    if (lines.length > 2) {
      setLines(lines.filter((_, i) => i !== index));
    }
  };

  const updateLine = (index: number, field: keyof JournalEntryLine, value: any) => {
    const newLines = [...lines];
    newLines[index] = { ...newLines[index], [field]: value };
    
    // Auto-clear the opposite field when one is entered
    if (field === 'debit' && value > 0) {
      newLines[index].credit = 0;
    } else if (field === 'credit' && value > 0) {
      newLines[index].debit = 0;
    }
    
    setLines(newLines);
  };

  const filteredEntries = entries.filter(entry => {
    const matchesSearch = 
      entry.entry_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.reference.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || entry.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(amount);
  };

  const { totalDebits, totalCredits, isBalanced } = calculateTotals();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg text-primary"></div>
          <p className="mt-4 text-lg">Loading Journal Entries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <FaBook className="text-primary" />
              Journal Entries
            </h1>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="btn btn-primary gap-2"
          >
            <FaPlus /> New Entry
          </button>
        </div>

        {/* Filters */}
        <div className="bg-base-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 flex-1">
              <span className="text-sm font-medium whitespace-nowrap">Search</span>
              <input
                type="text"
                placeholder="Search by entry number, description, or reference..."
                className="input input-bordered w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Status</span>
              <select
                className="select select-bordered"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="posted">Posted</option>
                <option value="voided">Voided</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Journal Entries Table */}
      <div className="bg-base-100 rounded-lg shadow overflow-hidden">
        <div className="card-body">
          {filteredEntries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table table-zebra">
                <thead>
                  <tr>
                    <th>Entry #</th>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Reference</th>
                    <th>Debits</th>
                    <th>Credits</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map(entry => (
                    <tr key={entry.id}>
                      <td className="font-mono font-bold">{entry.entry_number}</td>
                      <td>{moment(entry.date).format('DD/MM/YYYY')}</td>
                      <td>
                        <div className="font-semibold">{entry.description}</div>
                        <div className="text-xs opacity-60">
                          {entry.lines.length} line{entry.lines.length !== 1 ? 's' : ''}
                        </div>
                      </td>
                      <td className="text-sm opacity-60">{entry.reference || '-'}</td>
                      <td className="font-semibold text-success">{formatCurrency(entry.total_debits)}</td>
                      <td className="font-semibold text-error">{formatCurrency(entry.total_credits)}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className={`badge ${
                            entry.status === 'posted' ? 'badge-success' :
                            entry.status === 'voided' ? 'badge-error' :
                            'badge-warning'
                          }`}>
                            {entry.status}
                          </span>
                          {entry.is_balanced ? (
                            <FaCheckCircle className="text-success" title="Balanced" />
                          ) : (
                            <FaTimesCircle className="text-error" title="Unbalanced" />
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          {entry.status === 'draft' && (
                            <>
                              <button
                                onClick={() => handleEdit(entry)}
                                className="btn btn-sm btn-ghost text-primary"
                                title="Edit"
                              >
                                <FaEdit />
                              </button>
                              <button
                                onClick={() => handlePost(entry.id)}
                                className="btn btn-sm btn-ghost text-success"
                                title="Post"
                              >
                                <FaCheckCircle />
                              </button>
                              <button
                                onClick={() => handleDelete(entry.id)}
                                className="btn btn-sm btn-ghost text-error"
                                title="Delete"
                              >
                                <FaTrash />
                              </button>
                            </>
                          )}
                          {entry.status === 'posted' && (
                            <button
                              onClick={() => handleVoid(entry.id)}
                              className="btn btn-sm btn-ghost text-warning"
                              title="Void"
                            >
                              <FaTimesCircle />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 opacity-60">
              <FaBook className="text-6xl mx-auto mb-4" />
              <p className="text-lg">No journal entries found</p>
              <p className="text-sm">Create your first entry to get started</p>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal modal-open">
          <div className="modal-box max-w-4xl">
            <h3 className="font-bold text-lg mb-4">
              {editingEntry ? 'Edit Journal Entry' : 'New Journal Entry'}
            </h3>

            <div className="space-y-4">
              {/* Header Info */}
              <div className="grid grid-cols-3 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">Date *</span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  />
                </div>

                <div className="form-control col-span-2">
                  <label className="label">
                    <span className="label-text font-semibold">Reference</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Invoice #, Receipt #, etc."
                    className="input input-bordered"
                    value={formData.reference}
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Description *</span>
                </label>
                <input
                  type="text"
                  placeholder="Brief description of this entry..."
                  className="input input-bordered"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              {/* Journal Lines */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label-text font-semibold">Journal Lines</label>
                  <button onClick={addLine} className="btn btn-xs btn-outline gap-1">
                    <FaPlus /> Add Line
                  </button>
                </div>

                <div className="overflow-x-auto border border-base-300 rounded-lg">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th className="w-1/3">Account</th>
                        <th className="w-1/4">Description</th>
                        <th className="w-1/6 text-right">Debit</th>
                        <th className="w-1/6 text-right">Credit</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, index) => (
                        <tr key={index}>
                          <td>
                            <select
                              className="select select-bordered w-full"
                              value={line.account}
                              onChange={(e) => updateLine(index, 'account', e.target.value)}
                            >
                              <option value="">Select Account...</option>
                              {accounts.map(account => (
                                <option key={account.id} value={account.id}>
                                  {account.code} - {account.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              type="text"
                              placeholder="Line description..."
                              className="input input-sm input-bordered w-full"
                              value={line.description}
                              onChange={(e) => updateLine(index, 'description', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              className="input input-sm input-bordered w-full text-right"
                              value={line.debit || ''}
                              onChange={(e) => updateLine(index, 'debit', parseFloat(e.target.value) || 0)}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              className="input input-sm input-bordered w-full text-right"
                              value={line.credit || ''}
                              onChange={(e) => updateLine(index, 'credit', parseFloat(e.target.value) || 0)}
                            />
                          </td>
                          <td>
                            <button
                              onClick={() => removeLine(index)}
                              className="btn btn-xs btn-ghost text-error"
                              disabled={lines.length <= 2}
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="font-bold">
                        <td colSpan={2} className="text-right">Totals:</td>
                        <td className="text-right text-success">{formatCurrency(totalDebits)}</td>
                        <td className="text-right text-error">{formatCurrency(totalCredits)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Balance Check */}
                <div className={`alert mt-2 ${isBalanced ? 'alert-success' : 'alert-error'}`}>
                  <div className="flex items-center gap-2">
                    {isBalanced ? <FaCheckCircle /> : <FaTimesCircle />}
                    <span>
                      {isBalanced ? 'Entry is balanced' : `Entry is out of balance by ${formatCurrency(Math.abs(totalDebits - totalCredits))}`}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-action">
              <button onClick={handleCloseModal} className="btn btn-ghost">
                Cancel
              </button>
              <button 
                onClick={handleCreateOrUpdate} 
                className="btn btn-primary"
                disabled={!isBalanced}
              >
                {editingEntry ? 'Update' : 'Create'} Entry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalEntries;
